import { staffAuthHeaders } from '../../lib/sbAuth';
import { useState, useRef } from 'react';
import { buildTokenSignUrl, buildWaUrl, buildSignMessage, computeContractDuration } from '../../lib/invoiceHelpers';


const FINDRE_EMAIL = "info@findre.sa";

const NATIONALITIES = [
  "سعودي","مصري","يمني","سوري","باكستاني","هندي","بنغلاديشي","فلبيني","سوداني",
  "إثيوبي","أردني","لبناني","عراقي","تونسي","مغربي","جزائري","نيجيري","كيني",
  "إندونيسي","نيبالي","سريلانكي","أمريكي","بريطاني","فرنسي","أخرى"
];

export default function CustomerRegistrationTab({ tenants = [], units = [], contracts = [], invoices = [], addInvoice = ()=>{}, sbConfig = null }){
  const EMPTY = { fullName:"", nationality:"", idType:"هوية وطنية", idNumber:"", phone:"", idFrontImg:"", idBackImg:"", notes:"", unit:"", contractId:"", ejarNum:"", contractStart:"", contractEnd:"", rent:"", paymentMethod:"", deposit:"", lateDailyRate:"100" };
  // Build unit list from master contracts (source of truth).
  // Use unitNumber (raw) not unitName (which is "{unitNumber} - {district}" and would duplicate the district).
  // City defaults to Riyadh for any contract missing the field (older records).
  // The dropdown uses contract.id as the option value so duplicate unit numbers in different districts
  // (e.g. 306-العقيق and 306-الملقا) stay distinct. Ejar is carried forward for downstream matching.
  // Fall back to units state, then available tenants for legacy data.
  const vacantUnits = (contracts && contracts.length > 0)
    ? contracts
        .filter(c => c.status !== 'archived' && c.type !== 'storage')
        .map(c => ({
          id: c.id,
          ejarNum: c.ejarNum || '',
          unit: c.unitNumber || c.unit || c.unitName || '',
          district: c.district || '',
          city: c.city || 'الرياض',
        })).filter(u => u.unit)
    : (units && units.length > 0)
      ? units.map(u => ({ id: u.id, ejarNum: u.ejarNum || '', unit: u.unit || u.unitNumber, district: u.district || '', city: u.city || 'الرياض' }))
      : (tenants||[]).filter(t=>t.available).map(t => ({ ...t, ejarNum: t.ejarNum || '', city: t.city || 'الرياض' }));
  // Sequential invoice number — INV-000088, INV-000089, …
  // Counter starts at STARTING_SEQ; once real invoices exceed that, max+1 takes over.
  // Bump STARTING_SEQ here if you want to skip a range of numbers in the future.
  const STARTING_SEQ = 88;
  const maxSeq = (invoices||[]).reduce((m,i)=>Math.max(m, Number(i.seq)||0), 0);
  const nextSeq = Math.max(maxSeq + 1, STARTING_SEQ);
  const invoiceNo = `INV-${String(nextSeq).padStart(6,'0')}`;
  const [form,      setForm]      = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [pdfReady,  setPdfReady]  = useState(false);
  const [lastRecord, setLastRecord] = useState(null);
  const [waUrl,     setWaUrl]     = useState('');  // Pre-computed WhatsApp URL for the success-state button (must be ready synchronously so mobile doesn't block the pop-up)
  const frontRef = useRef(null);
  const backRef  = useRef(null);

  function handleImg(e, side){
    const file = e.target.files[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f=>({...f, [side]: ev.target.result}));
    reader.readAsDataURL(file);
  }

  function F(k,v){ setForm(f=>({...f,[k]:v})); }

  // Duplicate phone check — compare against all existing invoices
  const dupPhone = !!(form.phone && (invoices||[]).some(inv => inv.customerPhone === form.phone));

  // Resolve the selected contract once — used by conflict check, PDF builder, and the saved record.
  const selectedContract = (contracts||[]).find(c => c.id === form.contractId);
  const selectedDistrict = selectedContract?.district || '';

  // Duplicate unit check — block if there's any existing invoice for this CONTRACT
  // whose effective occupancy period overlaps with the new one.
  // "Effective end" = vacatedAt (if tenant left early) OR contractEnd (if still in lease).
  // Match strictly by contractId / ejarNum when present; fall back to unit+district for legacy invoices.
  // This prevents picking 306-الملقا from triggering a false "duplicate" against a 306-العقيق invoice.
  const conflictingInvoice = (form.unit && form.contractStart && form.contractEnd)
    ? (invoices||[]).find(inv => {
        const effectiveEnd = inv.vacatedAt || inv.contractEnd;
        const datesOverlap = (inv.contractStart || '') <= form.contractEnd &&
                             (effectiveEnd      || '') >= form.contractStart;
        if(!datesOverlap) return false;
        // Strict: same contract by id (or by Ejar)
        if (form.contractId && inv.contractId) return form.contractId === inv.contractId;
        if (form.ejarNum && inv.ejarNum)       return form.ejarNum   === inv.ejarNum;
        // Fallback: same unit; if both records carry district, require it to match too
        if (inv.unit !== form.unit) return false;
        if (inv.district && selectedDistrict) return inv.district === selectedDistrict;
        return true;
      })
    : null;
  const dupUnit = !!conflictingInvoice;

  const isValid = form.fullName && form.nationality && form.idNumber && form.phone
                && form.unit && form.rent && form.contractStart && form.contractEnd && form.paymentMethod
                && !dupPhone && !dupUnit;

  // ── Build PDF HTML ──────────────────────────────────────────────
  function buildPdfHtml(){
    const today  = new Date();
    const dateEN = today.toISOString().slice(0,10);
    const dateAR = today.toISOString().slice(0,10);
    // Resolve district + city from the matching contract (so PDF shows full address).
    // Prefer contractId/ejarNum from the form (unique per contract) so duplicate unit numbers don't collide.
    const matchedContract = selectedContract
      || (form.ejarNum && (contracts||[]).find(c => String(c.ejarNum||'').trim() === String(form.ejarNum||'').trim()))
      || (contracts||[]).find(c => (c.unitNumber||c.unitName||c.unit||'') === form.unit);
    const pdfDistrict = matchedContract ? (matchedContract.district || '') : '';
    const pdfCity     = matchedContract ? (matchedContract.city || 'الرياض') : 'الرياض';
    const pdfLocation = [pdfDistrict, pdfCity].filter(Boolean).join(' · ');
    // Contract duration (e.g. "شهر واحد" / "30 يوم")
    const pdfDuration = computeContractDuration(form.contractStart, form.contractEnd);
    // Brand colors
    const GOLD = "#ac9055";
    const NAVY = "#202a44";
    // Establishment info
    const COMP_AR  = "مؤسسة فايند آر إي للعقارات";
    const COMP_EN  = "FindRE Establishment For Real Estate";
    const CR       = "7043361620";
    const PHONE    = "0551817337";
    const COUNTRY_AR = "المملكة العربية السعودية";
    const COUNTRY_EN = "Kingdom of Saudi Arabia";
    // Official FindRE logo — inlined SVG so it renders inside blob: PDF window without needing external assets
    const LOGO_SVG = `<div style="text-align:center">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 351.03 405.82" style="height:70px;width:auto;display:block;margin:0 auto">
        <path fill="${GOLD}" d="M301.04,323.82c15.41-20.82,23.82-46.17,23.82-72.15v-110.99L162.43,0,0,140.67v232.42h203.44c22.97,0,45.08-6.37,64.36-18.49l51.22,51.21,32.02-32.02-49.99-49.99ZM45.28,161.36l117.16-101.46,117.16,101.46v90.31c0,41.99-34.16,76.15-76.15,76.15H45.28v-166.46Z"/>
        <polygon fill="${GOLD}" points="212.22 220.53 212.22 209.01 212.22 164.98 168.2 164.98 112.65 164.98 112.65 220.53 112.65 264.56 156.67 264.56 168.2 264.56 212.22 264.56 212.22 220.53"/>
      </svg>
      <div style="font-family:'Tajawal',Arial,sans-serif;font-weight:800;font-size:20px;color:${NAVY};letter-spacing:-.5px;margin-top:6px;line-height:1">FindRE</div>
    </div>`;
    // Numbers
    const rentN  = Number(form.rent)||0;
    const depN   = Number(form.deposit)||0;
    const lateN  = Number(form.lateDailyRate)||0;
    const subTot = rentN + depN;
    const vat    = 0; // residential rent VAT-exempt in KSA
    const total  = subTot + vat;
    const fmt    = n => Number(n).toLocaleString('en-US',{minimumFractionDigits:2, maximumFractionDigits:2});

    return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="UTF-8"/>
<title>Invoice ${invoiceNo}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  body{font-family:'Tajawal',Arial,sans-serif;background:#fff;color:${NAVY};padding:32px 40px;direction:rtl;font-size:12px}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:14px;border-bottom:1px solid #e8e0cc}
  .col-en{text-align:left;direction:ltr;line-height:1.5;font-size:11px;color:${NAVY}}
  .col-ar{text-align:right;direction:rtl;line-height:1.5;font-size:11px;color:${NAVY}}
  .col-en .name,.col-ar .name{font-weight:800;font-size:14px;margin-bottom:2px}
  .logo-wrap{flex:1;text-align:center;padding-top:6px}
  .invoice-title{text-align:center;font-size:24px;font-weight:800;color:${NAVY};margin:18px 0 22px;letter-spacing:-.5px}
  .invoice-title .ar{margin-left:8px}
  table{width:100%;border-collapse:collapse;margin-bottom:18px}
  .info-tbl td{padding:8px 12px;border:1px solid #d9d2c0;font-size:12px;vertical-align:top}
  .info-tbl td.lbl{background:#faf8f3;width:100px;font-weight:700;color:${NAVY}}
  .info-tbl td.lbl-en{background:#faf8f3;width:90px;font-weight:700;color:${NAVY};font-size:11px;direction:ltr;text-align:left}
  .items{margin-top:8px}
  .items th{background:${NAVY};color:#fff;padding:10px 8px;font-size:11px;font-weight:700;border:1px solid ${NAVY}}
  .items th .en{display:block;font-size:9px;font-weight:400;opacity:.85;direction:ltr}
  .items td{padding:10px 8px;border:1px solid #d9d2c0;font-size:12px;text-align:center}
  .items td.desc{text-align:right;font-weight:600}
  .items td.desc .en{display:block;font-size:10px;font-weight:400;color:#79858d;direction:ltr;text-align:left}
  .totals{display:flex;justify-content:flex-start;margin:14px 0 22px}
  .totals table{width:auto;min-width:300px}
  .totals td{padding:7px 14px;font-size:12px;border-bottom:1px solid #ece5d3}
  .totals td.lbl{font-weight:700;color:${NAVY}}
  .totals td.lbl .en{display:block;font-size:10px;font-weight:400;color:#79858d;direction:ltr;text-align:left}
  .totals td.amt{text-align:left;direction:ltr;font-weight:700}
  .totals tr.grand td{border-top:2px solid ${GOLD};border-bottom:none;background:#faf8f3;color:${GOLD}}
  h3{font-size:13px;font-weight:800;color:${NAVY};margin:18px 0 8px;border-right:3px solid ${GOLD};padding-right:8px}
  h3 .en{font-size:10px;font-weight:400;color:#79858d;margin-right:6px}
  .terms{background:#faf8f3;border:1px solid #e8e0cc;border-radius:6px;padding:10px 14px;font-size:11px;line-height:1.7;color:${NAVY};margin-bottom:14px}
  .terms ol{padding-right:18px}
  .terms li{margin-bottom:3px}
  .notes-box{border:1px solid #d9d2c0;border-radius:6px;padding:10px 14px;font-size:12px;min-height:40px;margin-bottom:18px;color:${NAVY}}
  .id-section{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:18px}
  .id-box{border:1px solid #d9d2c0;border-radius:6px;overflow:hidden;text-align:center}
  .id-box img{width:100%;height:140px;object-fit:cover;display:block}
  .id-box .id-label{font-size:10px;color:#79858d;padding:5px;background:#faf8f3;border-top:1px solid #ece5d3}
  .id-box .no-img{height:140px;display:flex;align-items:center;justify-content:center;color:#ccc;font-size:11px;background:#fafafa}
  .signatures{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:24px;page-break-inside:avoid}
  .sig-box{text-align:center}
  .sig-line{border-bottom:1.5px solid ${NAVY};margin:42px 18px 6px}
  .sig-label{font-size:11px;font-weight:700;color:${NAVY}}
  .sig-label .en{display:block;font-size:10px;font-weight:400;color:#79858d;margin-top:1px}
  .footer{margin-top:28px;padding-top:12px;border-top:1px solid #e8e0cc;text-align:center;font-size:10px;color:#79858d;line-height:1.6}
  .footer .name-line{color:${NAVY};font-weight:700}
  @media print{body{padding:18px 24px}@page{size:A4;margin:8mm}}
</style>
</head>
<body>

<div class="header">
  <div class="col-ar" style="flex:1">
    <div class="name">${COMP_AR}</div>
    <div>${COUNTRY_AR}</div>
    <div>${PHONE}</div>
    <div>رقم السجل التجاري ${CR}</div>
  </div>
  <div class="logo-wrap">${LOGO_SVG}</div>
  <div class="col-en" style="flex:1">
    <div class="name">${COMP_EN}</div>
    <div>${COUNTRY_EN}</div>
    <div>${PHONE}</div>
    <div>CR Number ${CR}</div>
  </div>
</div>

<div class="invoice-title">فاتورة <span class="ar">·</span> Invoice</div>

<table class="info-tbl">
  <tr>
    <td class="lbl">العميل</td>
    <td>${form.fullName||""}</td>
    <td class="lbl-en">Customer</td>
    <td style="direction:ltr;text-align:left">${form.fullName||""}</td>
  </tr>
  <tr>
    <td class="lbl">الجنسية</td>
    <td>${form.nationality||""}</td>
    <td class="lbl-en">Nationality</td>
    <td style="direction:ltr;text-align:left">${form.nationality||""}</td>
  </tr>
  <tr>
    <td class="lbl">رقم الهوية</td>
    <td style="direction:ltr;text-align:right">${form.idNumber||""}</td>
    <td class="lbl-en">ID Number</td>
    <td style="direction:ltr;text-align:left">${form.idNumber||""}</td>
  </tr>
  <tr>
    <td class="lbl">الهاتف</td>
    <td style="direction:ltr;text-align:right">${form.phone||""}</td>
    <td class="lbl-en">Phone</td>
    <td style="direction:ltr;text-align:left">${form.phone||""}</td>
  </tr>
  <tr>
    <td class="lbl">رقم الفاتورة</td>
    <td style="direction:ltr;text-align:right;font-weight:700;color:${GOLD}">${invoiceNo}</td>
    <td class="lbl-en">Invoice No.</td>
    <td style="direction:ltr;text-align:left;font-weight:700;color:${GOLD}">${invoiceNo}</td>
  </tr>
  <tr>
    <td class="lbl">التاريخ</td>
    <td style="direction:ltr;text-align:right">${dateEN}</td>
    <td class="lbl-en">Date</td>
    <td style="direction:ltr;text-align:left">${dateEN}</td>
  </tr>
  <tr>
    <td class="lbl">آلية الدفع</td>
    <td>${form.paymentMethod||""}</td>
    <td class="lbl-en">Payment</td>
    <td style="direction:ltr;text-align:left">${form.paymentMethod==='كاش'?'Cash':form.paymentMethod==='تحويل'?'Bank Transfer':''}</td>
  </tr>
  <tr>
    <td class="lbl">العقار</td>
    <td>${`وحدة ${form.unit||''}${pdfDistrict?` · ${pdfDistrict}`:''}${pdfCity?` · ${pdfCity}`:''}`}</td>
    <td class="lbl-en">Property</td>
    <td style="direction:ltr;text-align:left">${`Unit ${form.unit||''}${pdfDistrict?` · ${pdfDistrict}`:''}${pdfCity?` · ${pdfCity}`:''}`}</td>
  </tr>
  ${pdfDuration.ar ? `<tr>
    <td class="lbl">المدة</td>
    <td>${pdfDuration.ar}</td>
    <td class="lbl-en">Duration</td>
    <td style="direction:ltr;text-align:left">${pdfDuration.en}</td>
  </tr>`:''}
</table>

<table class="items">
  <tr>
    <th style="width:36px">#</th>
    <th>الوصف <span class="en">Description</span></th>
    <th style="width:60px">الكمية <span class="en">Qty</span></th>
    <th style="width:90px">السعر <span class="en">Unit Price</span></th>
    <th style="width:80px">الضريبة <span class="en">VAT</span></th>
    <th style="width:100px">المجموع <span class="en">Line Total</span></th>
  </tr>
  <tr>
    <td>1</td>
    <td class="desc">إيجار وحدة ${form.unit||""}${pdfLocation?` · ${pdfLocation}`:""} <span class="en">Unit ${form.unit||""}${pdfLocation?` · ${pdfLocation}`:""} rent · ${form.contractStart||""} → ${form.contractEnd||""}</span></td>
    <td>1</td>
    <td style="direction:ltr">${fmt(rentN)}</td>
    <td style="direction:ltr">0.00</td>
    <td style="direction:ltr;font-weight:700">${fmt(rentN)}</td>
  </tr>
  ${depN>0?`<tr>
    <td>2</td>
    <td class="desc">مبلغ التأمين (مسترد) <span class="en">Refundable security deposit</span></td>
    <td>1</td>
    <td style="direction:ltr">${fmt(depN)}</td>
    <td style="direction:ltr">0.00</td>
    <td style="direction:ltr;font-weight:700">${fmt(depN)}</td>
  </tr>`:""}
</table>

<div class="totals">
  <table>
    <tr><td class="lbl">المجموع الفرعي <span class="en">Subtotal</span></td><td class="amt">${fmt(subTot)} ر.س</td></tr>
    <tr><td class="lbl">ضريبة القيمة المضافة <span class="en">VAT</span></td><td class="amt">${fmt(vat)} ر.س</td></tr>
    <tr class="grand"><td class="lbl">الإجمالي <span class="en">Total</span></td><td class="amt">${fmt(total)} ر.س</td></tr>
  </table>
</div>

<h3>الشروط والأحكام <span class="en">Terms &amp; Conditions</span></h3>
<div class="terms">
  <ol>
    <li>يلتزم المستأجر بإخلاء الوحدة في تاريخ نهاية العقد (${form.contractEnd||"—"}) دون إشعار إضافي.<br/><span style="font-size:10px;color:#79858d;direction:ltr;display:inline-block">The tenant must vacate the unit by the contract end date without further notice.</span></li>
    <li>في حال عدم الإخلاء بعد نهاية العقد، يُحتسب على المستأجر مبلغ <b>${fmt(lateN)} ر.س</b> عن كل يوم تأخير.<br/><span style="font-size:10px;color:#79858d;direction:ltr;display:inline-block">A late fee of SAR ${fmt(lateN)} per day will apply for each day past the end date.</span></li>
    <li>مبلغ التأمين (إن وجد) يُسترد بعد الإخلاء وفحص الوحدة وخصم أي أضرار.<br/><span style="font-size:10px;color:#79858d;direction:ltr;display:inline-block">The deposit (if any) is refundable after vacating, inspection, and deduction of any damages.</span></li>
    <li>الإيجار مستحق الدفع في موعده الشهري بحسب آلية الدفع المتفق عليها.<br/><span style="font-size:10px;color:#79858d;direction:ltr;display:inline-block">Rent is due on its monthly date per the agreed payment method.</span></li>
  </ol>
</div>

${form.notes?`<h3>ملاحظات <span class="en">Notes</span></h3>
<div class="notes-box">${form.notes}</div>`:`<h3>ملاحظات <span class="en">Notes</span></h3><div class="notes-box">&nbsp;</div>`}

${(form.idFrontImg||form.idBackImg)?`
<h3>صورة الهوية <span class="en">ID Copy</span></h3>
<div class="id-section">
  <div class="id-box">
    ${form.idFrontImg?`<img src="${form.idFrontImg}"/>`:`<div class="no-img">الوجه الأمامي</div>`}
    <div class="id-label">الوجه الأمامي · Front</div>
  </div>
  <div class="id-box">
    ${form.idBackImg?`<img src="${form.idBackImg}"/>`:`<div class="no-img">الوجه الخلفي</div>`}
    <div class="id-label">الوجه الخلفي · Back</div>
  </div>
</div>`:""}

<div class="signatures">
  <div class="sig-box">
    <div class="sig-line"></div>
    <div class="sig-label">توقيع المستأجر <span class="en">Tenant Signature</span></div>
  </div>
  <div class="sig-box">
    <div class="sig-line"></div>
    <div class="sig-label">عن المؤسسة · ختم وتوقيع <span class="en">For ${COMP_EN} · Stamp &amp; Signature</span></div>
  </div>
</div>

<div style="display:flex;justify-content:center;gap:14px;margin-top:24px;page-break-inside:avoid;flex-wrap:wrap">
  <div style="text-align:center;border:1px solid #e8e0cc;border-radius:8px;padding:10px 14px;background:#faf8f3">
    <div style="font-size:10px;color:${NAVY};font-weight:700;margin-bottom:6px">البيانات الأساسية للسجل التجاري · Commercial Registration</div>
    <img src="${window.location.origin}/cr-qr.png" alt="CR QR" style="width:120px;height:120px;object-fit:contain;display:block;margin:0 auto"/>
  </div>
  <div style="text-align:center;border:1px solid #e8e0cc;border-radius:8px;padding:10px 14px;background:#faf8f3">
    <div style="font-size:10px;color:${NAVY};font-weight:700;margin-bottom:6px">رخصة فال · FAL Real Estate License</div>
    <img src="${window.location.origin}/fal-qr.png" alt="FAL QR" style="width:120px;height:120px;object-fit:contain;display:block;margin:0 auto"/>
    <div style="font-size:11px;color:${NAVY};font-weight:700;margin-top:6px;direction:ltr">1200035498</div>
  </div>
</div>

<div class="footer">
  <div class="name-line">${COMP_AR} · ${COMP_EN}</div>
  <div>${PHONE} · ${FINDRE_EMAIL}</div>
  <div style="margin-top:4px;font-size:9px">رقم الفاتورة · Invoice ${invoiceNo} — ${dateAR}</div>
</div>

</body></html>`;
  }

  function downloadPdf(){
    const html = buildPdfHtml();
    const blob = new Blob([html], {type:"text/html;charset=utf-8"});
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url,"_blank");
    if(win) setTimeout(()=>{ win.print(); }, 800);
    setPdfReady(true);
  }

  function sendEmail(){
    const body = encodeURIComponent(
`مرحباً،

تم تسجيل عميل جديد عبر منصة FindRE. التفاصيل:

الاسم الكامل: ${form.fullName}
الجنسية: ${form.nationality}
نوع الهوية: ${form.idType}
رقم الهوية: ${form.idNumber}
رقم الجوال: ${form.phone}
${form.unit?"رقم الوحدة: "+form.unit:""}
${form.contractStart?"بداية العقد: "+form.contractStart:""}
${form.contractEnd?"نهاية العقد: "+form.contractEnd:""}
${form.rent?"الإيجار الشهري: "+Number(form.rent).toLocaleString()+" ريال":""}
${form.deposit?"التأمين: "+Number(form.deposit).toLocaleString()+" ريال":""}
${form.paymentMethod?"آلية الدفع: "+form.paymentMethod:""}
رقم الفاتورة: ${invoiceNo}
${form.notes?"ملاحظات: "+form.notes:""}

تاريخ التسجيل: ${new Date().toISOString().slice(0,10)}
`);
    window.location.href = `mailto:${FINDRE_EMAIL}?subject=${encodeURIComponent("تسجيل عميل جديد — "+form.fullName)}&body=${body}`;
  }

  async function handleSubmit(){
    if(dupPhone){ alert("رقم الجوال مسجل مسبقاً. تحقق من الأرشيف قبل المتابعة."); return; }
    if(dupUnit){
      alert(`الوحدة ${form.unit} مؤجرة بالفعل خلال هذه الفترة (${conflictingInvoice.contractStart} → ${conflictingInvoice.contractEnd} · ${conflictingInvoice.customerName||''}). تحقق من الأرشيف قبل المتابعة.`);
      return;
    }
    // Find district + city from contracts (source of truth), fall back to tenants.
    // Prefer contractId/ejarNum so duplicate unit numbers in different districts pick the right contract.
    const matchedContract = selectedContract
      || (form.ejarNum && (contracts||[]).find(c => String(c.ejarNum||'').trim() === String(form.ejarNum||'').trim()))
      || (contracts||[]).find(c => (c.unitNumber||c.unitName||c.unit||'') === form.unit);
    const tenant = (tenants||[]).find(t=>t.unit===form.unit);
    const district = matchedContract ? (matchedContract.district||"") : (tenant ? tenant.district : "");
    const city     = matchedContract ? (matchedContract.city || "الرياض") : (tenant?.city || "الرياض");
    const rentN  = Number(form.rent)||0;
    const depN   = Number(form.deposit)||0;
    const total  = rentN + depN;
    const record = {
      id:           Date.now(),
      seq:          nextSeq,
      invoiceNo,
      date:         new Date().toISOString().slice(0,10),
      customerName: form.fullName,
      customerPhone:form.phone,
      idNumber:     form.idNumber,
      nationality:  form.nationality,
      unit:         form.unit,
      // Persist the precise contract reference so the archive + future lookups never collide
      // with same-numbered units in different districts.
      contractId:   form.contractId || (matchedContract?.id || ''),
      ejarNum:      form.ejarNum   || (matchedContract?.ejarNum || ''),
      district,
      city,
      rent:         rentN,
      deposit:      depN,
      total,
      paymentMethod:form.paymentMethod,
      contractStart:form.contractStart,
      contractEnd:  form.contractEnd,
      lateDailyRate:Number(form.lateDailyRate)||100,
    };
    // Build sign URL FIRST so we can store it on the invoice record — the archive
    // uses it later to send the customer their final signed+paid receipt.
    const signUrl = await buildTokenSignUrl({ ...record, fullName: form.fullName, phone: form.phone }, sbConfig);
    if(!signUrl){
      // Save the invoice regardless — losing the record would be worse than a
      // missing link, which can be resent from the Invoices tab.
      alert('تعذّر إنشاء رابط التوقيع. تم حفظ الفاتورة — أعد إرسال الرابط من صفحة الفواتير.');
    }
    record.signUrl = signUrl;

    addInvoice(record);
    setLastRecord(record);
    // Durable save: explicitly WAIT for the Supabase write to land before
    // showing the success page. Without this, mobile tabs can be killed
    // (e.g. when the user taps the WhatsApp link) before the fire-and-forget
    // sync completes, and the invoice is lost on next page load.
    if(sbConfig?.url && sbConfig?.anonKey){
      try {
        await fetch(`${sbConfig.url}/rest/v1/findre_data`, {
          method:  'POST',
          headers: await staffAuthHeaders(sbConfig, {
            'Content-Type': 'application/json',
            Prefer:         'resolution=merge-duplicates',
          }),
          body: JSON.stringify({ key: 'invoices', value: [...(invoices||[]), record] }),
        });
      } catch(e){
        console.warn('Invoice durable sync to Supabase failed:', e);
      }
    }
    setSubmitted(true);
    // Note: PDF auto-preview removed — customer reviews invoice on the sign page itself.
    // Ops can still tap "📄 تحميل PDF" on the success state if they want the file.
    const msg = buildSignMessage(form.fullName, invoiceNo, signUrl);
    setWaUrl(buildWaUrl(form.phone, msg));
  }

  function reset(){ setForm(EMPTY); setSubmitted(false); setPdfReady(false); setWaUrl(''); }

  // ── Input style helper ──
  const IS = (extra={}) => ({
    width:"100%", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:9, padding:"10px 14px", color:"#e2e8f0", fontSize:14, outline:"none",
    fontFamily:"'Tajawal',sans-serif", direction:"rtl", textAlign:"right", ...extra
  });

  const LBL = ({children,required}) => (
    <label style={{display:"block",fontSize:12,color:"#94a3b8",marginBottom:6}}>
      {children}{required&&<span style={{color:"#ef4444"}}> *</span>}
    </label>
  );

  return(
    <div style={{padding:"28px",maxWidth:860,margin:"0 auto"}}>
      <div style={{marginBottom:28}}>
        <h2 style={{fontSize:22,fontWeight:800,color:"#c8a03c",margin:0}}>📋 تسجيل عميل جديد</h2>
        <p style={{fontSize:13,color:"#475569",margin:"5px 0 0"}}>أدخل بيانات العميل وأرفق صورة الهوية — سيتم توليد ملف PDF تلقائياً وإرساله إلى FindRE</p>
      </div>

      {submitted ? (
        /* ── Success State ── */
        <div style={{textAlign:"center",padding:"48px 24px"}}>
          <div style={{fontSize:64,marginBottom:16}}>✅</div>
          <div style={{fontSize:22,fontWeight:800,color:"#22c55e",marginBottom:8}}>تم التسجيل بنجاح!</div>
          <div style={{fontSize:14,color:"#64748b",marginBottom:32}}>العميل: <strong style={{color:"#e2e8f0"}}>{form.fullName}</strong></div>

          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:32}}>
            {/* Use <a> with target=_blank so mobile (iOS Safari) opens WhatsApp directly.
                If waUrl is still being built (rare, network), fall back to async build on click. */}
            {waUrl ? (
              <a href={waUrl} target="_blank" rel="noopener noreferrer"
                style={{padding:"14px 32px",borderRadius:12,background:"linear-gradient(135deg,#25d366,#128c7e)",border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",display:"inline-flex",alignItems:"center",gap:8,textDecoration:"none"}}>
                📲 إرسال رابط التوقيع عبر واتساب
              </a>
            ) : (
              <button onClick={async ()=>{
                const signUrl = await buildTokenSignUrl({ ...lastRecord, fullName: form.fullName, phone: form.phone }, sbConfig);
                if(!signUrl){ alert('تعذّر إنشاء رابط التوقيع — حاول مرة أخرى بعد قليل.'); return; }
                const msg = buildSignMessage(form.fullName, lastRecord.invoiceNo, signUrl);
                window.location.href = buildWaUrl(form.phone, msg);
              }}
                style={{padding:"14px 32px",borderRadius:12,background:"linear-gradient(135deg,#25d366,#128c7e)",border:"none",color:"#fff",fontSize:15,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",display:"inline-flex",alignItems:"center",gap:8}}>
                📲 إرسال رابط التوقيع عبر واتساب
              </button>
            )}
            <button onClick={downloadPdf}
              style={{padding:"12px 28px",borderRadius:12,background:"linear-gradient(135deg,#c8a03c,#9a6f10)",border:"none",color:"#0d0f1a",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",display:"flex",alignItems:"center",gap:8}}>
              📄 تحميل PDF
            </button>
            <button onClick={sendEmail}
              style={{padding:"12px 24px",borderRadius:12,background:"rgba(56,189,248,0.12)",border:"1px solid rgba(56,189,248,0.3)",color:"#38bdf8",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif",display:"flex",alignItems:"center",gap:6}}>
              📧 إيميل لـ FindRE
            </button>
            <button onClick={reset}
              style={{padding:"12px 22px",borderRadius:12,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",color:"#64748b",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>
              + فاتورة جديدة
            </button>
          </div>

          <div style={{background:"rgba(56,189,248,0.06)",border:"1px solid rgba(56,189,248,0.18)",borderRadius:12,padding:"14px 20px",textAlign:"right",maxWidth:540,margin:"0 auto"}}>
            <div style={{fontSize:12,color:"#7dd3fc",fontWeight:700,marginBottom:6}}>📌 الخطوات</div>
            <div style={{fontSize:12,color:"#64748b",lineHeight:2}}>
              ١. تم فتح واتساب تلقائياً مع رابط التوقيع — اضغط ✓ للإرسال<br/>
              ٢. إذا لم يفتح واتساب، اضغط "📲 إرسال رابط التوقيع عبر واتساب" أعلاه<br/>
              ٣. بعد توقيع العميل، اضغط "🔄 تحديث التوقيعات" في الأرشيف لرؤية الحالة
            </div>
          </div>
        </div>
      ) : (
        /* ── Form ── */
        <div style={{display:"grid",gap:20}}>

          {/* Section: Personal Info */}
          <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"20px 22px"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#c8a03c",marginBottom:18,display:"flex",alignItems:"center",gap:8}}>
              👤 البيانات الشخصية
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

              <div style={{gridColumn:"1 / -1"}}>
                <LBL required>الاسم الكامل</LBL>
                <input value={form.fullName} onChange={e=>F("fullName",e.target.value)} placeholder="الاسم الرباعي كما في الهوية" style={IS()}/>
              </div>

              <div>
                <LBL required>الجنسية</LBL>
                <select value={form.nationality} onChange={e=>F("nationality",e.target.value)} style={IS()}>
                  <option value="">— اختر —</option>
                  {NATIONALITIES.map(n=><option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              <div>
                <LBL>رقم الجوال</LBL>
                <input value={form.phone} onChange={e=>F("phone",e.target.value)} placeholder="05xxxxxxxx"
                  style={IS({direction:"ltr",textAlign:"left", borderColor: dupPhone ? "#ef4444" : undefined})}/>
                {dupPhone && (
                  <div style={{marginTop:5,fontSize:12,color:"#ef4444",fontWeight:700,display:"flex",alignItems:"center",gap:5}}>
                    ⚠️ هذا الرقم مسجل مسبقاً في الأرشيف
                  </div>
                )}
              </div>

              <div>
                <LBL required>نوع الهوية</LBL>
                <select value={form.idType} onChange={e=>F("idType",e.target.value)} style={IS()}>
                  <option>هوية وطنية</option>
                  <option>إقامة</option>
                  <option>جواز سفر</option>
                </select>
              </div>

              <div>
                <LBL required>رقم الهوية / الإقامة</LBL>
                <input value={form.idNumber} onChange={e=>F("idNumber",e.target.value)} placeholder="1xxxxxxxxx" style={IS({direction:"ltr",textAlign:"left",letterSpacing:2})}/>
              </div>

            </div>
          </div>

          {/* Section: ID Upload */}
          <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"20px 22px"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#c8a03c",marginBottom:18,display:"flex",alignItems:"center",gap:8}}>
              🪪 صورة الهوية
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {[["idFrontImg",frontRef,"الوجه الأمامي"],["idBackImg",backRef,"الوجه الخلفي"]].map(([key,ref,label])=>(
                <div key={key}>
                  <LBL>{label}</LBL>
                  <input ref={ref} type="file" accept="image/*" onChange={e=>handleImg(e,key)} style={{display:"none"}}/>
                  <div onClick={()=>ref.current.click()}
                    style={{height:150,borderRadius:12,border:`2px dashed ${form[key]?"rgba(34,197,94,0.4)":"rgba(255,255,255,0.1)"}`,background:form[key]?"none":"rgba(255,255,255,0.02)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",overflow:"hidden",position:"relative",transition:"border-color .2s"}}>
                    {form[key]
                      ? <img src={form[key]} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      : <div style={{textAlign:"center",color:"#334155"}}>
                          <div style={{fontSize:28,marginBottom:6}}>📎</div>
                          <div style={{fontSize:12}}>انقر لرفع الصورة</div>
                        </div>
                    }
                    {form[key]&&<div style={{position:"absolute",top:6,left:6,background:"rgba(34,197,94,0.8)",color:"#fff",fontSize:10,padding:"2px 8px",borderRadius:6}}>✓ تم الرفع</div>}
                  </div>
                  {form[key]&&<button onClick={()=>F(key,"")} style={{marginTop:6,fontSize:11,color:"#ef4444",background:"none",border:"none",cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>✕ حذف الصورة</button>}
                </div>
              ))}
            </div>
          </div>

          {/* Section: Contract (mandatory) */}
          <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"20px 22px"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#c8a03c",marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
              🏠 بيانات العقد <span style={{fontSize:11,color:"#ef4444",fontWeight:700}}>(إلزامي)</span>
            </div>
            <div style={{fontSize:12,color:"#475569",marginBottom:16}}>كل الحقول مطلوبة لإكمال التسجيل</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div>
                <LBL required>رقم الوحدة</LBL>
                <select
                  value={form.contractId}
                  onChange={e=>{
                    const id = e.target.value;
                    const sel = vacantUnits.find(u => u.id === id);
                    setForm(f => sel
                      ? { ...f, contractId: sel.id, ejarNum: sel.ejarNum || '', unit: sel.unit }
                      : { ...f, contractId: '', ejarNum: '', unit: '' });
                  }}
                  style={IS({borderColor: dupUnit ? "#ef4444" : undefined})}>
                  <option value="">— اختر وحدة شاغرة —</option>
                  {vacantUnits.length===0
                    ? <option value="" disabled>لا توجد وحدات شاغرة حالياً</option>
                    : vacantUnits.map(t=>(
                        <option key={t.id} value={t.id}>وحدة {t.unit} · {t.district} · {t.city}</option>
                      ))
                  }
                </select>
                {dupUnit && (
                  <div style={{marginTop:6,fontSize:12,color:"#ef4444",fontWeight:700,lineHeight:1.6}}>
                    ⚠️ هذه الوحدة مؤجرة بالفعل خلال هذه الفترة
                    <div style={{fontSize:11,color:"#fca5a5",fontWeight:600,marginTop:2,direction:"ltr",textAlign:"right"}}>
                      {conflictingInvoice.contractStart} → {conflictingInvoice.contractEnd} · {conflictingInvoice.customerName||''}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <LBL required>الإيجار الشهري (ريال)</LBL>
                <input type="number" value={form.rent} onChange={e=>F("rent",e.target.value)} placeholder="0" style={IS({direction:"ltr",textAlign:"left"})}/>
              </div>
              <div>
                <LBL required>بداية العقد</LBL>
                <input type="date" value={form.contractStart} onChange={e=>F("contractStart",e.target.value)} style={IS({direction:"ltr",textAlign:"left"})}/>
              </div>
              <div>
                <LBL required>نهاية العقد</LBL>
                <input type="date" value={form.contractEnd} onChange={e=>F("contractEnd",e.target.value)} style={IS({direction:"ltr",textAlign:"left"})}/>
              </div>
              {/* Auto-computed duration — updates live as dates change */}
              {(() => {
                const d = computeContractDuration(form.contractStart, form.contractEnd);
                if(!d.ar) return null;
                return (
                  <div style={{gridColumn:"1 / -1",background:"rgba(200,160,60,0.08)",border:"1px solid rgba(200,160,60,0.25)",borderRadius:10,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                    <div style={{fontSize:12,color:"#94a3b8",fontWeight:700}}>⏱ مدة العقد · Duration</div>
                    <div style={{fontSize:14,fontWeight:800,color:"#c8a03c"}}>{d.ar} <span style={{fontSize:12,color:"#94a3b8",fontWeight:600,marginRight:6}}>· {d.en}</span></div>
                  </div>
                );
              })()}
              <div>
                <LBL required>آلية الدفع</LBL>
                <select value={form.paymentMethod} onChange={e=>F("paymentMethod",e.target.value)} style={IS()}>
                  <option value="">— اختر —</option>
                  <option value="كاش">كاش</option>
                  <option value="تحويل">تحويل بنكي</option>
                </select>
              </div>
              <div>
                <LBL>مبلغ التأمين (ريال)</LBL>
                <input type="number" value={form.deposit} onChange={e=>F("deposit",e.target.value)} placeholder="0" style={IS({direction:"ltr",textAlign:"left"})}/>
              </div>
              <div style={{gridColumn:"1 / -1"}}>
                <LBL>غرامة التأخير اليومية بعد انتهاء العقد (ريال)</LBL>
                <input type="number" value={form.lateDailyRate} onChange={e=>F("lateDailyRate",e.target.value)} placeholder="100" style={IS({direction:"ltr",textAlign:"left"})}/>
                <div style={{fontSize:11,color:"#64748b",marginTop:4}}>تطبق إذا لم يخرج المستأجر في تاريخ نهاية العقد</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:"20px 22px"}}>
            <LBL>ملاحظات إضافية</LBL>
            <textarea value={form.notes} onChange={e=>F("notes",e.target.value)} rows={3} placeholder="أي ملاحظات تخص العميل أو العقد…" style={IS({resize:"vertical"})}/>
          </div>

          <button onClick={handleSubmit} disabled={!isValid}
            style={{padding:"14px",borderRadius:12,background:isValid?"linear-gradient(135deg,#c8a03c,#9a6f10)":"rgba(255,255,255,0.05)",border:"none",color:isValid?"#0d0f1a":"#334155",fontSize:16,fontWeight:800,cursor:isValid?"pointer":"not-allowed",fontFamily:"'Tajawal',sans-serif",transition:"all .2s"}}>
            {isValid ? "✅ تسجيل العميل وتوليد PDF" : "يرجى تعبئة الحقول الإلزامية *"}
          </button>
        </div>
      )}
    </div>
  );
}
